import React, { useState, useCallback, useEffect, useRef } from "react";
import { Box, IconButton, useMediaQuery, useTheme } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import WaveSurfer from "wavesurfer.js";
import RecordPlugin from "wavesurfer.js/dist/plugins/record";
import { MicOnIcon, PauseIcon, SendIcon } from "../../../assets/icon";
import { useWhatsAppAudioRecorder } from "../../../hooks/useWhatsAppAudioRecorder";
import { formatVoiceClock } from "../../../hooks/useVoiceToText";
import { Message, User } from "../../../types";
import { StaticWaveform } from "../../wave-surfer-visualizer/static-waveform";
import { TelegramRecordingOverlay } from "../message-footer/telegram-recording-overlay/telegram-recording-overlay";
import {
  getSendActionContainerSx,
  getSendActionInnerSx,
} from "../message-footer/styles";
import {
  lockedSendButtonSx,
  sendTopActionStyle,
  VOICE_INPUT_HEIGHT,
} from "../message-footer/telegram-recording-overlay/styles";

const MOCK_USER: User = {
  id: "onboarding",
  is_bot: false,
  username: "user",
  first_name: "User",
  last_name: null,
  language_code: "en",
  email: "",
};

interface WhatsAppAudioRecorderProps {
  onCancel: () => void;
  isVisible: boolean;
  sendMessage?: React.Dispatch<React.SetStateAction<Message[]>>;
  sendMessageSocket?: (message: Message) => void;
  currentUser?: User;
  scrollToBottom?: () => void;
  isStatic?: boolean;
  // Render inside MessageFooter input row (keeps Lesson/Chat/MMM tabs).
  embedded?: boolean;
}

const MOCK_WAVEFORM_DATA = (() => {
  const length = 2048;
  const data = new Float32Array(length);
  for (let i = 0; i < length; i++) {
    data[i] = Math.sin((Math.PI * 2 * i) / length);
  }
  return data;
})();

interface WaveSurferVisualizerProps {
  isRecording: boolean;
  isPlaying: boolean;
  audioUrl?: string | null;
  audioRef?: React.RefObject<HTMLAudioElement | null>;
  isStatic?: boolean;
  waveHeight?: number;
  durationHint?: number;
}

const WaveSurferVisualizer: React.FC<WaveSurferVisualizerProps> = ({
  isRecording,
  isPlaying,
  audioUrl,
  audioRef,
  isStatic,
  waveHeight = 40,
  durationHint,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const waveSurferRef = useRef<WaveSurfer | null>(null);
  const recordPluginRef = useRef<ReturnType<typeof RecordPlugin.create> | null>(
    null
  );
  const isSeekingRef = useRef<boolean>(false);
  const syncIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const blobUrlRef = useRef<string | null>(null);
  const audioDurationRef = useRef<number>(0);
  const durationHintRef = useRef<number>(durationHint ?? 0);
  const barWidth = waveHeight <= 20 ? 1.5 : 2;
  const barGap = waveHeight <= 20 ? 1.5 : 2;

  useEffect(() => {
    durationHintRef.current =
      typeof durationHint === "number" &&
      durationHint > 0 &&
      Number.isFinite(durationHint)
        ? durationHint
        : 0;
  }, [durationHint]);

  useEffect(() => {
    if (isStatic && isRecording) return;
    if (!containerRef.current) return;

    const dummyCanvas = document.createElement("canvas");
    const dummyCtx = dummyCanvas.getContext("2d");
    if (!dummyCtx) return;

    const gradient = dummyCtx.createLinearGradient(10, 10, 300, 10);
    gradient.addColorStop(0, "#28C9DC");
    gradient.addColorStop(1, "#7B49C1");

    const resolveDuration = (wavesurfer: WaveSurfer): number => {
      const candidates = [
        audioRef?.current?.duration,
        audioDurationRef.current,
        wavesurfer.getDuration(),
        durationHintRef.current,
      ];
      for (const value of candidates) {
        if (typeof value === "number" && value > 0 && Number.isFinite(value)) {
          return value;
        }
      }
      return 0;
    };

    if (isRecording) {
      const wavesurfer = WaveSurfer.create({
        container: containerRef.current,
        waveColor: gradient,
        progressColor: gradient,
        cursorWidth: 0,
        height: waveHeight,
        barWidth,
        barGap,
        barRadius: 2,
        hideScrollbar: true,
      });

      const recordPlugin = RecordPlugin.create({
        renderRecordedAudio: false,
        scrollingWaveform: true,
        continuousWaveform: false,
        continuousWaveformDuration: 200,
      });

      wavesurfer.registerPlugin(recordPlugin);
      waveSurferRef.current = wavesurfer;
      recordPluginRef.current = recordPlugin;

      const startRecording = async () => {
        if (recordPlugin.isRecording()) {
          recordPlugin.stopRecording();
        }
        await recordPlugin.startRecording();
      };

      startRecording().catch((err) => {
        console.error("Could not start recording:", err);
      });

      return () => {
        recordPlugin.stopRecording();
        recordPlugin.stopMic();
        wavesurfer.destroy();
      };
    } else if (audioUrl) {
      const wavesurfer = WaveSurfer.create({
        container: containerRef.current,
        waveColor: gradient,
        progressColor: gradient,
        cursorWidth: 2,
        cursorColor: "#E6E6E680",
        height: waveHeight,
        barWidth,
        barGap,
        barRadius: 2,
        backend: "MediaElement",
        fillParent: true,
        dragToSeek: false,
        interact: false,
      });

      waveSurferRef.current = wavesurfer;

      const preloadAndLoad = async () => {
        try {
          const response = await fetch(audioUrl);
          const blob = await response.blob();
          blobUrlRef.current = URL.createObjectURL(blob);

          // MediaRecorder webm/mp4 often exposes Infinity on <audio>.duration —
          // decode once so the playhead can advance while reviewing.
          try {
            const AudioCtx =
              window.AudioContext ||
              (window as unknown as { webkitAudioContext: typeof AudioContext })
                .webkitAudioContext;
            if (AudioCtx) {
              const ctx = new AudioCtx();
              const buffer = await ctx.decodeAudioData(await blob.arrayBuffer());
              if (buffer.duration > 0 && Number.isFinite(buffer.duration)) {
                audioDurationRef.current = buffer.duration;
              }
              void ctx.close();
            }
          } catch {
            // Fall back to metadata / durationHint below.
          }

          const preloadAudio = new Promise<void>((resolve) => {
            const tempAudio = new Audio();
            tempAudio.preload = "metadata";
            tempAudio.src = blobUrlRef.current!;

            const onLoadedMetadata = () => {
              if (
                tempAudio.duration > 0 &&
                Number.isFinite(tempAudio.duration) &&
                audioDurationRef.current <= 0
              ) {
                audioDurationRef.current = tempAudio.duration;
              }
              tempAudio.removeEventListener("loadedmetadata", onLoadedMetadata);
              tempAudio.removeEventListener("error", onError);
              resolve();
            };

            const onError = () => {
              tempAudio.removeEventListener("loadedmetadata", onLoadedMetadata);
              tempAudio.removeEventListener("error", onError);
              resolve();
            };

            tempAudio.addEventListener("loadedmetadata", onLoadedMetadata);
            tempAudio.addEventListener("error", onError);
            tempAudio.load();

            setTimeout(() => {
              if (
                tempAudio.duration > 0 &&
                Number.isFinite(tempAudio.duration) &&
                audioDurationRef.current <= 0
              ) {
                audioDurationRef.current = tempAudio.duration;
              }
              tempAudio.removeEventListener("loadedmetadata", onLoadedMetadata);
              tempAudio.removeEventListener("error", onError);
              resolve();
            }, 500);
          });

          await preloadAudio;

          if (
            audioDurationRef.current <= 0 &&
            durationHintRef.current > 0
          ) {
            audioDurationRef.current = durationHintRef.current;
          }

          wavesurfer.load(blobUrlRef.current).catch((err: unknown) => {
            if ((err as DOMException).name !== "AbortError") {
              console.error(`WaveSurfer load error:`, err);
            }
          });
        } catch (error) {
          console.warn("Audio preload failed, loading directly:", error);
          if (durationHintRef.current > 0) {
            audioDurationRef.current = durationHintRef.current;
          }
          wavesurfer.load(audioUrl).catch((err: unknown) => {
            if ((err as DOMException).name !== "AbortError") {
              console.error(`WaveSurfer load error:`, err);
            }
          });
        }
      };

      preloadAndLoad();

      const syncVisualization = () => {
        if (!audioRef?.current || !wavesurfer || isSeekingRef.current) return;

        const currentTime = audioRef.current.currentTime;
        const duration = resolveDuration(wavesurfer);

        if (
          duration > 0 &&
          Number.isFinite(currentTime) &&
          currentTime >= 0
        ) {
          const progress = Math.min(1, Math.max(0, currentTime / duration));
          if (Number.isFinite(progress)) {
            try {
              wavesurfer.seekTo(progress);
            } catch (err) {
              console.warn("WaveSurfer seekTo error:", err);
            }
          }
        }
      };

      syncIntervalRef.current = setInterval(syncVisualization, 50);

      const performSeek = (e: MouseEvent | TouchEvent) => {
        if (!containerRef.current) return;

        const rect = containerRef.current.getBoundingClientRect();
        if (rect.width === 0) return;

        const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
        const clickX = clientX - rect.left;
        const progress = Math.max(0, Math.min(1, clickX / rect.width));

        if (!isFinite(progress)) return;

        const preloadedDuration = audioDurationRef.current;
        const wsDuration = wavesurfer.getDuration();
        const audioDuration = audioRef?.current?.duration ?? 0;
        const hintDuration = durationHintRef.current;

        let duration = 0;
        for (const candidate of [
          preloadedDuration,
          wsDuration,
          audioDuration,
          hintDuration,
        ]) {
          if (
            typeof candidate === "number" &&
            candidate > 0 &&
            Number.isFinite(candidate)
          ) {
            duration = candidate;
            break;
          }
        }

        if (duration > 0 && isFinite(duration)) {
          const seekTime = progress * duration;
          if (isFinite(seekTime) && seekTime >= 0 && seekTime <= duration) {
            isSeekingRef.current = true;
            try {
              if (audioRef?.current) {
                audioRef.current.currentTime = seekTime;
              }

              if (isFinite(progress) && progress >= 0 && progress <= 1) {
                wavesurfer.seekTo(progress);
              }
            } catch (err) {
              console.warn("Audio seek error:", err);
            }

            setTimeout(() => {
              isSeekingRef.current = false;
            }, 200);
          }
        }
      };

      let isDragging = false;
      let hasMoved = false;

      const handleContainerMouseDown = (e: MouseEvent | TouchEvent) => {
        isDragging = true;
        hasMoved = false;
        if ("touches" in e) {
          e.preventDefault();
          e.stopPropagation();
        }
        performSeek(e);
      };

      const handleContainerMouseMove = (e: MouseEvent | TouchEvent) => {
        if (isDragging) {
          hasMoved = true;
          if ("touches" in e) {
            e.preventDefault();
            e.stopPropagation();
          }
          performSeek(e);
        }
      };

      const handleContainerMouseUp = (e?: MouseEvent | TouchEvent) => {
        if (e && "touches" in e) {
          e.preventDefault();
          e.stopPropagation();
        }
        isDragging = false;
        hasMoved = false;
        isSeekingRef.current = false;
      };

      const handleContainerClick = (e: MouseEvent | TouchEvent) => {
        if (hasMoved) {
          if (e.preventDefault) {
            e.preventDefault();
          }
          if (e.stopPropagation) {
            e.stopPropagation();
          }
          return;
        }
        performSeek(e);
      };

      const container = containerRef.current;
      if (container) {
        container.addEventListener("mousedown", handleContainerMouseDown, true);
        container.addEventListener("mousemove", handleContainerMouseMove, true);
        container.addEventListener("mouseup", handleContainerMouseUp, true);
        container.addEventListener("mouseleave", handleContainerMouseUp, true);
        container.addEventListener("click", handleContainerClick, true);
        container.addEventListener("touchstart", handleContainerMouseDown, {
          passive: false,
          capture: true,
        });
        container.addEventListener("touchmove", handleContainerMouseMove, {
          passive: false,
          capture: true,
        });
        container.addEventListener("touchend", handleContainerMouseUp, {
          passive: false,
          capture: true,
        });
      }

      wavesurfer.on("ready", () => {
        const wsDuration = wavesurfer.getDuration();
        if (
          wsDuration > 0 &&
          isFinite(wsDuration) &&
          audioDurationRef.current === 0
        ) {
          audioDurationRef.current = wsDuration;
        }
      });

      return () => {
        if (syncIntervalRef.current) {
          clearInterval(syncIntervalRef.current);
          syncIntervalRef.current = null;
        }
        if (blobUrlRef.current) {
          URL.revokeObjectURL(blobUrlRef.current);
          blobUrlRef.current = null;
        }
        audioDurationRef.current = 0;
        if (container) {
          container.removeEventListener("click", handleContainerClick, true);
          container.removeEventListener(
            "mousedown",
            handleContainerMouseDown,
            true
          );
          container.removeEventListener(
            "mousemove",
            handleContainerMouseMove,
            true
          );
          container.removeEventListener(
            "mouseup",
            handleContainerMouseUp,
            true
          );
          container.removeEventListener(
            "mouseleave",
            handleContainerMouseUp,
            true
          );
          container.removeEventListener(
            "touchstart",
            handleContainerMouseDown,
            {
              capture: true,
            } as EventListenerOptions
          );
          container.removeEventListener("touchmove", handleContainerMouseMove, {
            capture: true,
          } as EventListenerOptions);
          container.removeEventListener("touchend", handleContainerMouseUp, {
            capture: true,
          } as EventListenerOptions);
        }
        wavesurfer.destroy();
      };
    }
  }, [isStatic, isRecording, audioUrl, audioRef, waveHeight, barWidth, barGap]);

  if (isStatic && isRecording) {
    return (
      <Box
        sx={{
          width: "100%",
          height: waveHeight,
          borderRadius: "20px",
          overflow: "hidden",
        }}
      >
        <StaticWaveform
          mockData={MOCK_WAVEFORM_DATA}
          height={waveHeight}
          barWidth={barWidth}
          barGap={barGap}
        />
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        width: "100%",
        height: `${waveHeight}px`,
        borderRadius: "20px",
        overflow: "hidden",
        cursor: !isRecording && audioUrl ? "pointer" : "default",
        userSelect: "none",
        WebkitUserSelect: "none",
      }}
    />
  );
};

export { WaveSurferVisualizer as AudioWaveformVisualizer };


export const WhatsAppAudioRecorder: React.FC<WhatsAppAudioRecorderProps> = ({
  onCancel,
  isVisible,
  sendMessage = () => {},
  sendMessageSocket = () => {},
  currentUser = MOCK_USER,
  scrollToBottom,
  isStatic = false,
  embedded = false,
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const {
    isRecording,
    isPaused,
    isProcessing,
    recordTimeMs,
    recordedAudioBlob,
    pausedPreviewUrl,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    cancelRecording,
    sendRecordedAudio,
  } = useWhatsAppAudioRecorder({
    sendMessage,
    sendMessageSocket,
    currentUser,
    scrollToBottom,
  });

  const [wasCancelled, setWasCancelled] = useState(false);
  const [wasSent, setWasSent] = useState(false);
  const [pendingSend, setPendingSend] = useState(false);
  const pendingSendRef = useRef(false);

  const displayTimeMs = isStatic ? 12000 : recordTimeMs;
  const showRecorderUi =
    isStatic || isRecording || isPaused || isProcessing || pendingSend;

  useEffect(() => {
    if (
      !isStatic &&
      isVisible &&
      !isRecording &&
      !isPaused &&
      !isProcessing &&
      !recordedAudioBlob &&
      !wasCancelled &&
      !wasSent
    ) {
      const isPWA = window.matchMedia("(display-mode: standalone)").matches;
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      const delay = isPWA && isIOS ? 500 : 100;

      const timer = setTimeout(() => {
        void startRecording();
      }, delay);
      return () => clearTimeout(timer);
    }
  }, [
    isStatic,
    isVisible,
    isRecording,
    isPaused,
    isProcessing,
    recordedAudioBlob,
    startRecording,
    wasCancelled,
    wasSent,
  ]);

  useEffect(() => {
    if (isVisible) {
      setWasCancelled(false);
      setWasSent(false);
      setPendingSend(false);
      pendingSendRef.current = false;
    }
  }, [isVisible]);

  useEffect(() => {
    if (!pendingSendRef.current || !recordedAudioBlob) return;
    pendingSendRef.current = false;
    setPendingSend(false);
    void (async () => {
      setWasSent(true);
      await sendRecordedAudio();
      onCancel();
    })();
  }, [recordedAudioBlob, sendRecordedAudio, onCancel]);

  const handleCancel = useCallback(async () => {
    if (isStatic) return;
    setWasCancelled(true);
    pendingSendRef.current = false;
    setPendingSend(false);
    await cancelRecording();
    onCancel();
  }, [cancelRecording, isStatic, onCancel]);

  const handleSend = useCallback(() => {
    if (isStatic || isProcessing) return;
    if (isRecording || isPaused) {
      pendingSendRef.current = true;
      setPendingSend(true);
      stopRecording();
      return;
    }
    if (recordedAudioBlob) {
      pendingSendRef.current = true;
      setPendingSend(true);
      setWasSent(true);
      void (async () => {
        await sendRecordedAudio();
        onCancel();
      })();
    }
  }, [
    isStatic,
    isProcessing,
    isRecording,
    isPaused,
    recordedAudioBlob,
    stopRecording,
    sendRecordedAudio,
    onCancel,
  ]);

  if (!isVisible || !showRecorderUi) return null;

  const recorderBody = (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        width: "100%",
        minHeight: VOICE_INPUT_HEIGHT,
        px: embedded ? 0 : 0.5,
        overflow: "visible",
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0, position: "relative" }}>
        <TelegramRecordingOverlay
          recordTimeMs={displayTimeMs}
          clockTime={formatVoiceClock(displayTimeMs)}
          cancelProgress={0}
          isLocked
          isPaused={isStatic ? false : isPaused}
          isCancelling={false}
          dragOffsetX={0}
          pausedPreviewUrl={pausedPreviewUrl}
          durationSeconds={Math.max(0.1, displayTimeMs / 1000)}
          onCancelLocked={handleCancel}
        />

        <Box sx={getSendActionContainerSx({ locked: true })}>
          <Box sx={getSendActionInnerSx(false)}>
            <IconButton
              type="button"
              aria-label={isPaused ? "Resume recording" : "Pause recording"}
              disabled={isStatic || isProcessing}
              onClick={isPaused ? resumeRecording : pauseRecording}
              sx={sendTopActionStyle()}
            >
              {isPaused ? (
                <MicOnIcon
                  style={{
                    width: 16,
                    height: 16,
                    color: "currentColor",
                  }}
                />
              ) : (
                <PauseIcon size={16} />
              )}
            </IconButton>
            <IconButton
              type="button"
              aria-label="Send voice message"
              disabled={isStatic || isProcessing}
              onClick={handleSend}
              sx={lockedSendButtonSx(isSmallScreen)}
            >
              <SendIcon
                width={isSmallScreen ? "20px" : "24px"}
                height={isSmallScreen ? "20px" : "24px"}
              />
            </IconButton>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  if (embedded) {
    return recorderBody;
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 12 }}
        transition={{ duration: 0.25, ease: "easeOut" }}
        style={{ width: "100%", maxWidth: 750, margin: "0 auto 12px" }}
      >
        {recorderBody}
      </motion.div>
    </AnimatePresence>
  );
};

export default WhatsAppAudioRecorder;
